import { BadgeVariant } from '@Components';
import { ScheduleStatus } from '@Models';

const SCHEDULE_STATUS_VARIANT: Record<ScheduleStatus, BadgeVariant> = {
  active: 'green',
  paused: 'yellow',
  ended: 'gray',
};

const SCHEDULE_NAME_MAX_LENGTH = 255;

const COMPACT_SELECT_TRIGGER_CLASS_NAME =
  'h-9 rounded-lg border-gray-300 bg-white px-3 text-xs font-semibold text-gray-900';

const getScheduleStatusVariant = (status: ScheduleStatus): BadgeVariant => SCHEDULE_STATUS_VARIANT[status] ?? 'gray';

const getErrorResponse = (error: unknown): object | null => {
  const response = typeof error === 'object' && error !== null && 'response' in error ? error.response : undefined;
  return typeof response === 'object' && response !== null ? response : null;
};

const getErrorDetailValue = (error: unknown): unknown => {
  const response = getErrorResponse(error);
  const data = response !== null && 'data' in response ? response.data : undefined;
  return typeof data === 'object' && data !== null && 'detail' in data ? data.detail : undefined;
};

const getErrorDetail = (error: unknown): string | null => {
  const detail = getErrorDetailValue(error);
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object' && detail !== null && 'message' in detail) {
    const { message } = detail as { message: unknown };
    return typeof message === 'string' ? message : null;
  }
  return null;
};

const getErrorCode = (error: unknown): string | null => {
  const detail = getErrorDetailValue(error);
  if (typeof detail === 'object' && detail !== null && 'code' in detail) {
    const { code } = detail as { code: unknown };
    return typeof code === 'string' ? code : null;
  }
  return null;
};

const getErrorStatus = (error: unknown): number | null => {
  const response = getErrorResponse(error);
  const status = response !== null && 'status' in response ? response.status : undefined;
  return typeof status === 'number' ? status : null;
};

const getConflictMessage = (
  error: unknown,
  t: (key: string) => string,
  i18n: { exists: (key: string) => boolean },
  fallbackKey: string,
): string => {
  const code = getErrorCode(error);
  const key = code === null ? null : `Schedule.Configuration.Conflict.${code}`;
  if (key !== null && i18n.exists(key)) return t(key);
  return getErrorDetail(error) ?? t(fallbackKey);
};

export {
  COMPACT_SELECT_TRIGGER_CLASS_NAME,
  getConflictMessage,
  getErrorCode,
  getErrorDetail,
  getErrorStatus,
  SCHEDULE_NAME_MAX_LENGTH,
};
export default getScheduleStatusVariant;
